import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { safeJson } from '../../utils/safeJson';
import { API } from '../../config/api';
import {
  Search,
  Trash2,
  Music,
  Video,
  FileText,
  MessageCircle,
  Loader2,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Eye
} from 'lucide-react';

const CONTENT_TYPES = [
  { key: 'posts', label: 'Posts', icon: FileText },
  { key: 'tracks', label: 'Tracks', icon: Music },
  { key: 'videos', label: 'Videos', icon: Video },
  { key: 'reels', label: 'Clips/Reels', icon: MessageCircle },
];

export default function ContentManagementPanel({ getAuthHeaders, hasPermission, isPlatformOwner }) {
  const [contentType, setContentType] = useState('posts');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const limit = 20;

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        content_type: contentType,
        q: search,
        limit: limit.toString(),
        skip: (page * limit).toString(),
      });
      const res = await fetch(`${API}/admin/control-center/content?${params}`, {
        headers: getAuthHeaders(),
      });
      const result = await safeJson(res);
      if (result.ok && result.data?.items) {
        setItems(result.data.items);
        setTotal(result.data.total || 0);
      }
    } catch (e) {
      toast.error('Failed to load content');
    }
    setLoading(false);
  }, [contentType, search, page, getAuthHeaders]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleDelete = async (item) => {
    const idFieldMap = { posts: 'post_id', tracks: 'track_id', videos: 'video_id', reels: 'reel_id' };
    const id = item[idFieldMap[contentType]];
    try {
      const res = await fetch(`${API}/admin/control-center/content/${contentType}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const result = await safeJson(res);
      if (result.ok && result.data?.success) {
        toast.success(result.data.message || 'Content deleted');
        fetchContent();
      } else {
        toast.error(result.data?.detail || result.error || 'Delete failed');
      }
    } catch (e) {
      toast.error('Delete failed');
    }
    setDeleteConfirm(null);
  };

  const getItemTitle = (item) => {
    if (contentType === 'posts') return item.text?.slice(0, 80) || 'No text';
    return item.title || item.caption || 'Untitled';
  };

  const getItemId = (item) => {
    const m = { posts: 'post_id', tracks: 'track_id', videos: 'video_id', reels: 'reel_id' };
    return item[m[contentType]] || 'N/A';
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Card className="p-6" data-testid="content-management-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Content Management</h2>
        <Badge variant="outline">{total} items</Badge>
      </div>

      {/* Content type tabs */}
      <div className="flex gap-2 mb-4">
        {CONTENT_TYPES.map(ct => (
          <Button
            key={ct.key}
            variant={contentType === ct.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setContentType(ct.key); setPage(0); }}
            data-testid={`content-tab-${ct.key}`}
          >
            <ct.icon className="w-4 h-4 mr-1" />
            {ct.label}
          </Button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder={`Search ${contentType}...`}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="pl-9"
          data-testid="content-search-input"
        />
      </div>

      {/* Content table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3 text-sm">Content</th>
                <th className="text-left p-3 text-sm">Author</th>
                <th className="text-left p-3 text-sm">Created</th>
                <th className="text-left p-3 text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={getItemId(item) || i} className="border-b border-border/50 hover:bg-muted/20" data-testid={`content-row-${i}`}>
                  <td className="p-3">
                    <div className="max-w-md">
                      <p className="font-medium text-sm truncate">{getItemTitle(item)}</p>
                      <p className="text-xs text-muted-foreground">ID: {getItemId(item)}</p>
                      {item.genre && <Badge variant="outline" className="mt-1 text-xs">{item.genre}</Badge>}
                    </div>
                  </td>
                  <td className="p-3">
                    <div>
                      <p className="text-sm">{item._author_name || item.author_name || item.artist_name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">{item._author_email || ''}</p>
                      {item._author_handle && <p className="text-xs text-muted-foreground">@{item._author_handle}</p>}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-muted-foreground">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      {item.audio_url && (
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" asChild>
                          <a href={item.audio_url} target="_blank" rel="noopener noreferrer" title="Listen">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        </Button>
                      )}
                      {hasPermission('delete_content') && (
                        <>
                          {deleteConfirm === getItemId(item) ? (
                            <div className="flex gap-1">
                              <Button size="sm" variant="destructive" className="h-7 text-xs px-2" onClick={() => handleDelete(item)} data-testid={`confirm-delete-${i}`}>
                                Confirm
                              </Button>
                              <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => setDeleteConfirm(null)}>
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-red-500 hover:bg-red-500/10"
                              onClick={() => setDeleteConfirm(getItemId(item))}
                              title="Delete"
                              data-testid={`delete-content-${i}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">No {contentType} found.</div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="content-prev-page">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="content-next-page">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
